import { z } from 'zod';

/**
 * `realtime.v1`: realtime voice calls, shaped like OpenAI's GPT-Live sessions over WebRTC
 * (`POST /v1/live/sessions`) so that any provider speaking that API fits. The caller holds the
 * connection and its media; the core drives the call on its sideband (the call delegates tasks to
 * the client, which answers them); the provider only signs the call: an SDP offer in, the answer
 * and where to attach out. Voices depend on the provider (`voices.list`).
 */
export const realtimeVoiceSchema = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[A-Za-z0-9._-]+$/);

/** A message the call starts with, as context: never spoken as it is. */
export const realtimeInputSchema = z
  .object({
    role: z.enum(['user', 'assistant', 'developer']),
    text: z.string().min(1).max(16_000),
  })
  .strict();

/**
 * The events a WebRTC call speaks: `v1`, OpenAI's GPT-Live session API (delegations carry no
 * words; answers are `session.commentary.append`), or `v2`, the ChatGPT app's newer protocol
 * (`OpenAI-Alpha: quicksilver=v2`: delegations carry the request's text; answers are
 * `delegation.context.append`).
 */
export const realtimeProtocolSchema = z.enum(['v1', 'v2']);

/**
 * Where a server attaches to the call to drive it (its sideband): a path under the API of the
 * provider's connection named, which the core reaches with that connection's own credentials.
 */
export const realtimeSidebandSchema = z
  .object({
    connection: z.string().min(1).max(256),
    path: z
      .string()
      .min(1)
      .max(1024)
      .regex(/^[A-Za-z0-9._~/-]+$/),
  })
  .strict();

export const realtimeV1 = {
  'call.create': {
    input: z
      .object({
        sdp: z.string().min(1).max(1_000_000),
        session: z
          .object({
            instructions: z.string().max(32_000).optional(),
            /** Messages before the call: up to 128 (the API's limit). */
            input: z.array(realtimeInputSchema).max(128).optional(),
            /** One of the provider's voices (`voices.list`); its default otherwise. */
            voice: realtimeVoiceSchema.optional(),
            /** What the user speaks (ISO 639-1), when known; the provider detects it otherwise. */
            language: z
              .string()
              .regex(/^[a-z]{2}$/)
              .optional(),
          })
          .strict()
          .default({}),
      })
      .strict(),
    output: z
      .object({
        transport: z.literal('webrtc'),
        sdp: z.string().min(1).max(1_000_000),
        /** The call's session id (`live_…`, `rtc_…`); null when the provider does not name it. */
        sessionId: z.string().min(1).max(256).nullable(),
        protocol: realtimeProtocolSchema.default('v1'),
        /** Where the core attaches to drive the call. */
        sideband: realtimeSidebandSchema.optional(),
      })
      .strict(),
  },
  /** The voices calls speak with, from the provider, and the one it uses by default. */
  'voices.list': {
    input: z.object({}).strict(),
    output: z
      .object({
        voices: z
          .array(z.object({ id: realtimeVoiceSchema, name: z.string().min(1).max(256) }).strict())
          .max(1000),
        default: realtimeVoiceSchema.nullable(),
      })
      .strict(),
  },
} as const;
